import { getDataSource } from '@/lib/data-source';
import { SubscriptionPlan, MultiCurrencyPrice, MultiLanguageString, MultiLanguageStringArray, TranslatedSubscriptionPlan, TranslationParams } from '@/types/billing';

// Request/Response Types matching JSON:API format
export interface SubscriptionPlanAttributes {
  name: string;
  nameTranslations?: MultiLanguageString;
  description?: string;
  descriptionTranslations?: MultiLanguageString;
  type: 'individual' | 'teams';
  isRecommended?: boolean;
  isAvailableInCheckout?: boolean;
  status: 'draft' | 'active' | 'archived';
  freeTrialDays?: number;
  trialRequiresCard?: boolean;
  prorateChanges?: boolean;
  allowCancellation?: boolean;
  displayOrder?: number;
  includedSeats?: number; // For teams type
  minSeats?: number; // For teams type
  maxSeats?: number; // For teams type
  createdAt?: string;
  updatedAt?: string;
}

export interface PlanPricingData {
  billingCycleId: string;
  prices: MultiCurrencyPrice;
  pricesPerAdditionalSeat?: MultiCurrencyPrice; // For teams
  defaultCurrency?: string;
  stripeProductId?: string;
  stripePriceIds?: { [currency: string]: string };
  priceIds?: { [currency: string]: string }; // For including existing price IDs in updates
  introPrices?: MultiCurrencyPrice;
  recurringPrices?: MultiCurrencyPrice;
  introCycles?: number;
}

export interface PlanCreditSettingsData {
  includeCredits: boolean;
  creditsPerCycle?: number;
  allowTopUp: boolean;
  topUpPricesPerCredit?: MultiCurrencyPrice;
}

export interface PlanFeaturesData {
  features: string[];
  featuresTranslations?: MultiLanguageStringArray;
}

export interface SubscriptionPlanCreateData {
  name: string;
  description?: string;
  type?: 'individual' | 'teams';
  includedSeats?: number; // Required for teams
  minSeats?: number;
  maxSeats?: number;
  isRecommended?: boolean;
  isAvailableInCheckout?: boolean;
  status?: 'draft' | 'active' | 'archived';
  freeTrialDays?: number;
  trialRequiresCard?: boolean;
  prorateChanges?: boolean;
  allowCancellation?: boolean;
  pricing?: PlanPricingData[];
  creditSettings?: PlanCreditSettingsData;
  features?: PlanFeaturesData;
  featureFlags?: { [key: string]: boolean };
  parentPlanId?: string;
  translations?: {
    name?: MultiLanguageString;
    description?: MultiLanguageString;
    features?: Array<{ [locale: string]: string }>;
  };
}

export interface SubscriptionPlanUpdateData extends Partial<SubscriptionPlanCreateData> {}

export interface SubscriptionPlanFilters {
  type?: 'individual' | 'teams';
  status?: 'active' | 'draft' | 'archived';
  recommended?: boolean;
  isVariant?: boolean;
  parentPlanId?: string;
  isDefault?: boolean;
}

export interface SubscriptionPlanSortOptions {
  field?: 'display-order' | 'name' | '-created-at';
}

export interface SubscriptionPlanPagination {
  page?: number;
  perPage?: number;
}

export interface SubscriptionPlanListResponse {
  data: SubscriptionPlan[];
  meta?: {
    pagination?: {
      total: number;
      count: number;
      perPage: number;
      currentPage: number;
      totalPages: number;
    };
  };
  jsonapi: {
    version: string;
  };
}

class SubscriptionPlansService {
  private dataSource = getDataSource();

  /**
   * Format translation parameters for URL query string
   */
  private formatTranslationParams(params?: TranslationParams): URLSearchParams {
    const urlParams = new URLSearchParams();
    
    if (params?.lang) {
      urlParams.append('lang', params.lang);
    }
    
    if (params?.includeTranslations !== undefined) {
      if (typeof params.includeTranslations === 'boolean') {
        urlParams.append('include_translations', params.includeTranslations ? 'true' : 'false');
      } else if (typeof params.includeTranslations === 'string') {
        urlParams.append('include_translations', params.includeTranslations);
      } else if (Array.isArray(params.includeTranslations)) {
        urlParams.append('include_translations', params.includeTranslations.join(','));
      }
    }
    
    return urlParams;
  }

  /**
   * Get all subscription plans for a product
   */
  async getSubscriptionPlans(
    productId: string,
    filters?: SubscriptionPlanFilters,
    sort?: SubscriptionPlanSortOptions,
    include?: string[],
    translationParams?: TranslationParams,
    pagination?: SubscriptionPlanPagination
  ): Promise<TranslatedSubscriptionPlan[]> {
    try {
      const params = new URLSearchParams();
      
      // Add filters
      if (filters?.type) params.append('filter[type]', filters.type);
      if (filters?.status) params.append('filter[status]', filters.status);
      if (filters?.recommended !== undefined) params.append('filter[recommended]', filters.recommended.toString());
      if (filters?.isVariant !== undefined) params.append('filter[is-variant]', filters.isVariant.toString());
      if (filters?.parentPlanId) params.append('filter[parent-plan-id]', filters.parentPlanId);
      if (filters?.isDefault !== undefined) params.append('filter[is-default]', filters.isDefault.toString());
      
      // Add sorting
      if (sort?.field) params.append('sort', sort.field);
      
      // Add includes
      if (include && include.length > 0) {
        params.append('include', include.join(','));
      }

      // Add pagination (Laravel-style)
      if (pagination?.page !== undefined) params.append('page', pagination.page.toString());
      if (pagination?.perPage !== undefined) params.append('per_page', pagination.perPage.toString());
      
      // Add translation parameters
      const translationUrlParams = this.formatTranslationParams(translationParams);
      translationUrlParams.forEach((value, key) => {
        params.append(key, value);
      });
      
      const queryString = params.toString();
      const url = `/api/v1/products/${productId}/billing/subscription-plans${queryString ? `?${queryString}` : ''}`;
      
      console.log('Get subscription plans - URL:', url);
      console.log('Get subscription plans - filters:', filters);
      
      const response = await this.dataSource.get<SubscriptionPlanListResponse>(url);
      
      console.log('Get subscription plans response:', response);
      
      // The response.data is already deserialized by the LiveAdapter
      if (response.data && typeof response.data === 'object') {
        const responseData = response.data as unknown as Record<string, unknown>;
        // Check if it's already an array of plans
        if (Array.isArray(responseData)) {
          return responseData as TranslatedSubscriptionPlan[];
        }
        // Check for data property
        if ('data' in responseData && Array.isArray(responseData.data)) {
          return responseData.data as TranslatedSubscriptionPlan[];
        }
      }
      
      return [];
    } catch (error) {
      console.error(`Error fetching subscription plans for product ${productId}:`, error);
      throw error;
    }
  }

  /**
   * Get a single subscription plan
   */
  async getSubscriptionPlan(
    productId: string, 
    planId: string,
    translationParams?: TranslationParams
  ): Promise<TranslatedSubscriptionPlan> {
    try {
      const params = this.formatTranslationParams(translationParams);
      // Add include parameter for feature flags and other relationships
      // Try including prices relationship to get price IDs for existing prices
      params.append('include', 'pricing,prices,credit-settings,features,feature-flags');
      const queryString = params.toString();
      const url = `/api/v1/products/${productId}/billing/subscription-plans/${planId}${queryString ? `?${queryString}` : ''}`;
      
      const response = await this.dataSource.get<{ data: TranslatedSubscriptionPlan }>(url);
      
      console.log('Get subscription plan response:', response);
      console.log('Get subscription plan URL:', url);
      
      // The response.data is already deserialized by the LiveAdapter
      if (response.data && typeof response.data === 'object') {
        const responseData = response.data as unknown as Record<string, unknown>;
        // If it has an id and name, it's already the plan object
        if (responseData.id && responseData.name) {
          console.log('Get subscription plan - Direct plan object with featureFlags:', (responseData as { featureFlags?: Record<string, boolean> }).featureFlags);
          return responseData as unknown as TranslatedSubscriptionPlan;
        }
        // Otherwise check for data property
        if ('data' in responseData) {
          const dataObj = (responseData as { data: TranslatedSubscriptionPlan }).data;
          console.log('Get subscription plan - Plan in data property with featureFlags:', dataObj.featureFlags);
          return dataObj as TranslatedSubscriptionPlan;
        }
      }
      
      throw new Error('Invalid response format');
    } catch (error) {
      console.error(`Error fetching subscription plan ${planId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new subscription plan
   */
  async createSubscriptionPlan(
    productId: string,
    data: SubscriptionPlanCreateData
  ): Promise<SubscriptionPlan> {
    try {
      console.log('createSubscriptionPlan - Input data:', data);
      
      // Prepare the request body in JSON:API format
    const requestBody = {
        data: {
          type: 'subscription-plans',
          attributes: {
            name: data.name,
            ...(data.description !== undefined && { description: data.description }),
            ...(data.type !== undefined && { type: data.type }),
            ...(data.includedSeats !== undefined && { 'included-seats': data.includedSeats }),
            ...(data.minSeats !== undefined && { 'min-seats': data.minSeats }),
            ...(data.maxSeats !== undefined && { 'max-seats': data.maxSeats }),
            ...(data.isRecommended !== undefined && { 'is-recommended': data.isRecommended }),
            ...(data.isAvailableInCheckout !== undefined && {
              'is-available-in-checkout': data.isAvailableInCheckout,
            }),
            ...(data.status !== undefined && { status: data.status }),
            ...(data.freeTrialDays !== undefined && { 'free-trial-days': data.freeTrialDays }),
            ...(data.trialRequiresCard !== undefined && { 'trial-requires-card': data.trialRequiresCard }),
            ...(data.prorateChanges !== undefined && { 'prorate-changes': data.prorateChanges }),
            ...(data.allowCancellation !== undefined && { 'allow-cancellation': data.allowCancellation }),
            ...(data.parentPlanId && { 'parent-plan-id': data.parentPlanId }),
            ...(data.parentPlanId && { 'is-default': false }),
            ...(data.translations && { translations: data.translations })
          },
          relationships: {} as Record<string, unknown>
        }
      };

      // Add pricing relationship if provided
      if (data.pricing && data.pricing.length > 0) {
        console.log('updateSubscriptionPlan - Processing pricing data with price IDs:');
        data.pricing.forEach((pricing, index) => {
          console.log(`  Cycle ${index}: ${pricing.billingCycleId}`, {
            prices: pricing.prices,
            priceIds: pricing.priceIds
          });
        });
        
        requestBody.data.relationships.pricing = {
          data: data.pricing.map((pricing) => ({
            type: 'plan-pricing',
            attributes: {
              'billing-cycle-id': pricing.billingCycleId,
              prices: pricing.prices,
              'prices-per-additional-seat': pricing.pricesPerAdditionalSeat,
              ...(pricing.introPrices && { 'intro-prices': pricing.introPrices }),
              ...(pricing.recurringPrices && { 'recurring-prices': pricing.recurringPrices }),
              ...(pricing.introCycles !== undefined && { 'intro-cycles': pricing.introCycles }),
              // Include price IDs for updating existing prices
              ...(pricing.priceIds && Object.keys(pricing.priceIds).length > 0 && {
                'price-ids': pricing.priceIds
              })
            }
          }))
        };
      }

      // Add credit settings if provided
      if (data.creditSettings) {
        requestBody.data.relationships['credit-settings'] = {
          data: {
            type: 'plan-credit-settings',
            attributes: {
              'include-credits': data.creditSettings.includeCredits,
              'credits-per-cycle': data.creditSettings.creditsPerCycle,
              'allow-top-up': data.creditSettings.allowTopUp,
              'top-up-prices-per-credit': data.creditSettings.topUpPricesPerCredit
            }
          }
        };
      }

      // Add features if provided
      console.log('Subscription plan - data.features:', data.features);
      if (data.features) {
        console.log('Subscription plan - features array:', data.features.features);
        console.log('Subscription plan - features length:', data.features.features?.length);
        requestBody.data.relationships.features = {
          data: {
            type: 'plan-features',
            attributes: {
              features: data.features.features || [],
              'features-translations': data.features.featuresTranslations
            }
          }
        };
      }

      // Add feature flags if provided
      if (data.featureFlags && Object.keys(data.featureFlags).length > 0) {
        requestBody.data.relationships['feature-flags'] = {
          data: {
            type: 'plan-feature-flags',
            attributes: data.featureFlags
          }
        };
      }

      console.log('createSubscriptionPlan - Request body being sent:', JSON.stringify(requestBody, null, 2));

      const response = await this.dataSource.post<{ data: SubscriptionPlan }>(
        `/api/v1/products/${productId}/billing/subscription-plans`,
        requestBody
      );
      
      console.log('Create subscription plan response:', response);
      
      // The response.data is already deserialized by the LiveAdapter
      // Check if it's wrapped in a data property or directly the plan
      if (response.data && typeof response.data === 'object') {
        // If it has an id and name, it's already the plan object
        const responseData = response.data as unknown as Record<string, unknown>;
        if (responseData.id && responseData.name) {
          console.log('Response is already deserialized plan:', responseData);
          return responseData as unknown as SubscriptionPlan;
        }
        // Otherwise check for data property
        if ('data' in responseData) {
          console.log('Response has data property:', responseData.data);
          return responseData.data as SubscriptionPlan;
        }
      }
      
      throw new Error('Invalid response format');
    } catch (error) {
      console.error(`Error creating subscription plan:`, error);
      throw error;
    }
  }

  /**
   * Update a subscription plan (full update)
   */
  async updateSubscriptionPlan(
    productId: string,
    planId: string,
    data: SubscriptionPlanUpdateData
  ): Promise<SubscriptionPlan> {
    try {
      console.log('updateSubscriptionPlan - Input data:', data);
      
      const requestBody = {
        data: {
          type: 'subscription-plans',
          id: planId,
          attributes: {
            ...(data.name !== undefined && { name: data.name }),
            ...(data.description !== undefined && { description: data.description }),
            ...(data.isRecommended !== undefined && { 'is-recommended': data.isRecommended }),
            ...(data.isAvailableInCheckout !== undefined && {
              'is-available-in-checkout': data.isAvailableInCheckout,
            }),
            ...(data.status !== undefined && { status: data.status }),
            ...(data.freeTrialDays !== undefined && { 'free-trial-days': data.freeTrialDays }),
            ...(data.trialRequiresCard !== undefined && { 'trial-requires-card': data.trialRequiresCard }),
            ...(data.prorateChanges !== undefined && { 'prorate-changes': data.prorateChanges }),
            ...(data.allowCancellation !== undefined && { 'allow-cancellation': data.allowCancellation }),
            ...(data.includedSeats !== undefined && { 'included-seats': data.includedSeats }),
            ...(data.minSeats !== undefined && { 'min-seats': data.minSeats }),
            ...(data.maxSeats !== undefined && { 'max-seats': data.maxSeats }),
            ...(data.translations && { translations: data.translations })
          },
          relationships: {} as Record<string, unknown>
        }
      };

      // Add pricing relationship if provided
      if (data.pricing && data.pricing.length > 0) {
        console.log('updateSubscriptionPlan - Processing pricing data with price IDs:');
        data.pricing.forEach((pricing, index) => {
          console.log(`  Cycle ${index}: ${pricing.billingCycleId}`, {
            prices: pricing.prices,
            priceIds: pricing.priceIds
          });
        });
        
        requestBody.data.relationships.pricing = {
          data: data.pricing.map((pricing) => ({
            type: 'plan-pricing',
            attributes: {
              'billing-cycle-id': pricing.billingCycleId,
              prices: pricing.prices,
              'prices-per-additional-seat': pricing.pricesPerAdditionalSeat,
              ...(pricing.introPrices && { 'intro-prices': pricing.introPrices }),
              ...(pricing.recurringPrices && { 'recurring-prices': pricing.recurringPrices }),
              ...(pricing.introCycles !== undefined && { 'intro-cycles': pricing.introCycles }),
              // Include price IDs for updating existing prices
              ...(pricing.priceIds && Object.keys(pricing.priceIds).length > 0 && {
                'price-ids': pricing.priceIds
              })
            }
          }))
        };
      }

      // Add credit settings if provided
      if (data.creditSettings) {
        requestBody.data.relationships['credit-settings'] = {
          data: {
            type: 'plan-credit-settings',
            attributes: {
              'include-credits': data.creditSettings.includeCredits,
              'credits-per-cycle': data.creditSettings.creditsPerCycle,
              'allow-top-up': data.creditSettings.allowTopUp,
              'top-up-prices-per-credit': data.creditSettings.topUpPricesPerCredit
            }
          }
        };
      }

      // Add features if provided
      console.log('Subscription plan - data.features:', data.features);
      if (data.features) {
        console.log('Subscription plan - features array:', data.features.features);
        console.log('Subscription plan - features length:', data.features.features?.length);
        requestBody.data.relationships.features = {
          data: {
            type: 'plan-features',
            attributes: {
              features: data.features.features || [],
              'features-translations': data.features.featuresTranslations
            }
          }
        };
      }

      // Add feature flags if provided
      if (data.featureFlags && Object.keys(data.featureFlags).length > 0) {
        requestBody.data.relationships['feature-flags'] = {
          data: {
            type: 'plan-feature-flags',
            attributes: data.featureFlags
          }
        };
      }

      console.log('updateSubscriptionPlan - Request body being sent:', JSON.stringify(requestBody, null, 2));

      const response = await this.dataSource.put<{ data: SubscriptionPlan }>(
        `/api/v1/products/${productId}/billing/subscription-plans/${planId}`,
        requestBody
      );
      
      console.log('Update subscription plan response:', response);
      
      // The response.data is already deserialized by the LiveAdapter
      if (response.data && typeof response.data === 'object') {
        const responseData = response.data as unknown as Record<string, unknown>;
        if (responseData.id && responseData.name) {
          return responseData as unknown as SubscriptionPlan;
        }
        if ('data' in responseData) {
          return responseData.data as SubscriptionPlan;
        }
      }
      
      throw new Error('Invalid response format');
    } catch (error) {
      console.error(`Error updating subscription plan ${planId}:`, error);
      throw error;
    }
  }

  /**
   * Partially update a subscription plan
   */
  async patchSubscriptionPlan(
    productId: string,
    planId: string,
    data: Partial<SubscriptionPlanUpdateData>
  ): Promise<SubscriptionPlan> {
    try {
      const requestBody = {
        data: {
          type: 'subscription-plans',
          id: planId,
          attributes: {
            ...(data.status && { status: data.status }),
            ...(data.isRecommended !== undefined && { 'is-recommended': data.isRecommended }),
            ...(data.isAvailableInCheckout !== undefined && {
              'is-available-in-checkout': data.isAvailableInCheckout,
            }),
            ...(data.freeTrialDays !== undefined && { 'free-trial-days': data.freeTrialDays }),
            ...(data.trialRequiresCard !== undefined && { 'trial-requires-card': data.trialRequiresCard }),
            ...(data.translations && { translations: data.translations })
          }
        }
      };

      const response = await this.dataSource.patch<{ data: SubscriptionPlan }>(
        `/api/v1/products/${productId}/billing/subscription-plans/${planId}`,
        requestBody
      );
      
      console.log('Patch subscription plan response:', response);
      
      // The response.data is already deserialized by the LiveAdapter
      if (response.data && typeof response.data === 'object') {
        const responseData = response.data as unknown as Record<string, unknown>;
        if (responseData.id && responseData.name) {
          return responseData as unknown as SubscriptionPlan;
        }
        if ('data' in responseData) {
          return responseData.data as SubscriptionPlan;
        }
      }
      
      throw new Error('Invalid response format');
    } catch (error) {
      console.error(`Error patching subscription plan ${planId}:`, error);
      throw error;
    }
  }

  /**
   * Delete a subscription plan
   */
  async deleteSubscriptionPlan(productId: string, planId: string): Promise<void> {
    try {
      await this.dataSource.delete(
        `/api/v1/products/${productId}/billing/subscription-plans/${planId}`
      );
    } catch (error) {
      console.error(`Error deleting subscription plan ${planId}:`, error);
      throw error;
    }
  }

  /**
   * Archive a subscription plan (safer than delete)
   */
  async archiveSubscriptionPlan(productId: string, planId: string): Promise<SubscriptionPlan> {
    return this.patchSubscriptionPlan(productId, planId, { status: 'archived' });
  }
}

export const subscriptionPlansService = new SubscriptionPlansService();
